import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, MapPin, Star } from "lucide-react";
import Footer from "../ui/Footer";
import { MobileActionBar } from "../ui/Navbar";
import UnitCard from "../ui/UnitCard";
import InquiryForm from "../ui/InquiryForm";
import Modal from "../ui/Modal";
import Lightbox from "../ui/Lightbox";
import { CallButton, MessengerButtons } from "../ui/ContactLinks";
import { availability } from "../ui/ProjectCard";
import { getAllUnits, getProjects, getReviews } from "../firebase/api";
import { useAsync } from "../lib/hooks";
import { compareUnits, unitStatus, unitSummary } from "../lib/format";
import { SITE, mapEmbedSrc } from "../config/site";
import logo from "../assets/img/logo.svg";
import heroBuilding from "../assets/pixel/hero-building.gif";
import { useI18n } from "../i18n";
import { useFormat } from "../i18n/format";
import Seo from "../seo/Seo";
import "../scss/landing.scss";

export default function Landing() {
  const { t, lp, field } = useI18n();
  const f = useFormat();
  const navigate = useNavigate();
  const projects = useAsync(getProjects, []);
  const units = useAsync(
    () => (projects.data ? getAllUnits(projects.data) : Promise.resolve(null)),
    [projects.data]
  );
  const reviews = useAsync(getReviews, []);
  const [inquiryUnit, setInquiryUnit] = useState(null);
  const [gallery, setGallery] = useState(null);
  const railRef = useRef(null);

  const projectById = useMemo(
    () => Object.fromEntries((projects.data || []).map((p) => [p.id, p])),
    [projects.data]
  );
  const available = useMemo(
    () => (units.data || []).filter((u) => unitStatus(u) === "slobodan").sort(compareUnits),
    [units.data]
  );
  const freeTotal = (projects.data || []).reduce((s, p) => s + availability(p).freeFlats, 0);
  const minPrice = projects.data?.[0]?.najniza_cena;

  const goToThanks = (values) =>
    navigate(lp("/hvala"), { state: { lead: true, source: "landing", interesovanje: values?.interesovanje } });

  const scrollRail = (dir) => {
    const rail = railRef.current;
    if (rail) rail.scrollBy({ left: dir * rail.clientWidth * 0.85, behavior: "smooth" });
  };

  const rooms = available.map((u) => parseFloat(u.struktura)).filter((n) => n > 0);
  const roomRange =
    rooms.length > 1 && Math.min(...rooms) !== Math.max(...rooms)
      ? t("landing.roomRange", { from: f.strukturaPlural(Math.min(...rooms)), to: f.strukturaPlural(Math.max(...rooms)) })
      : t("landing.roomRangeFallback");

  const bullets = [
    roomRange,
    minPrice ? t("landing.priceFrom", { price: minPrice }) : t("landing.priceFallback"),
    projects.loading ? t("landing.checkFree") : t("landing.stillFree", { label: t("project.freeFlats", { count: freeTotal }) }),
    t("landing.direct"),
  ];

  const review = reviews.data?.[0];

  return (
    <div className="landing">
      <Seo
        title={t("seo.landing.title")}
        description={t("seo.landing.description")}
        path="/ponuda"
        ready={Boolean(units.data) && !reviews.loading}
      />
      <header className="landing-header">
        <div className="container landing-header-inner">
          <Link to={lp("/")} aria-label={t("nav.logoHome")}>
            <img src={logo} alt="Tehnočin DOO Invest" width="110" height="72" />
          </Link>
          <CallButton className="btn btn--sm" source="landing-zaglavlje">
            {SITE.phoneDisplay}
          </CallButton>
        </div>
      </header>

      <main>
        <section className="landing-hero">
          <div className="container landing-hero-inner">
            <div className="landing-hero-copy">
              <img src={heroBuilding} alt={t("home.hero.art")} className="pixel landing-hero-art" width="96" height="72" fetchpriority="high" />
              <p className="eyebrow">{t("landing.eyebrow")}</p>
              <h1>{t("landing.title")}</h1>
              <ul className="landing-bullets">
                {bullets.map((b) => (
                  <li key={b}>
                    <Check size={18} /> {b}
                  </li>
                ))}
              </ul>
              <div className="landing-hero-actions">
                <CallButton className="btn btn--accent btn--lg" source="landing-hero">
                  {t("landing.callNow", { phone: SITE.phoneDisplay })}
                </CallButton>
                <MessengerButtons source="landing-hero" className="btn btn--ghost btn--lg" text={t("landing.messengerText")} />
              </div>
            </div>

            <div className="landing-form-card" id="upit">
              <h2>{t("landing.formTitle")}</h2>
              <p>{t("landing.formText")}</p>
              <InquiryForm
                source="landing"
                idPrefix="landing"
                showInterest
                submitLabel={t("landing.submit")}
                messagePlaceholder={t("landing.messagePlaceholder")}
                onSuccess={goToThanks}
              />
              {review && (
                <figure className="landing-review">
                  <span className="stars" aria-label={t("home.reviews.rating", { value: review.rating })}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} size={14} fill={i <= review.rating ? "currentColor" : "none"} />
                    ))}
                  </span>
                  <blockquote>„{field(review, "text")}“</blockquote>
                  <figcaption>{t("landing.reviewBy", { author: review.author })}</figcaption>
                </figure>
              )}
            </div>
          </div>
        </section>

        <section className="section section--surface landing-units">
          <div className="container">
            <div className="section-head section-head--split">
              <div>
                <p className="eyebrow">{t("landing.unitsEyebrow")}</p>
                <h2>{t("landing.unitsTitle")}</h2>
              </div>
              {available.length > 1 && (
                <div className="rail-controls">
                  <button type="button" className="icon-btn" onClick={() => scrollRail(-1)} aria-label={t("landing.prev")}>
                    <ChevronLeft size={22} />
                  </button>
                  <button type="button" className="icon-btn" onClick={() => scrollRail(1)} aria-label={t("landing.next")}>
                    <ChevronRight size={22} />
                  </button>
                </div>
              )}
            </div>
            {!units.data || units.loading ? (
              <div className="rail">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="skeleton rail-skeleton" />
                ))}
              </div>
            ) : available.length ? (
              <div className="rail" ref={railRef} tabIndex={0} aria-label={t("landing.railLabel")}>
                {available.map((unit) => (
                  <UnitCard
                    key={`${unit.projectId}-${unit.id}`}
                    unit={unit}
                    project={projectById[unit.projectId]}
                    onInquire={setInquiryUnit}
                    onOpenGallery={(u) => setGallery({ images: u.imageUrls, title: f.unitSummary(u, projectById[u.projectId]) })}
                  />
                ))}
              </div>
            ) : (
              <p className="empty-state">{t("landing.noUnits")}</p>
            )}
            <p className="landing-units-more">
              <Link to={lp("/katalog")} className="link">
                {t("landing.fullCatalogue")}
              </Link>
            </p>
          </div>
        </section>

        <section className="section landing-location">
          <div className="container landing-location-inner">
            <div>
              <p className="eyebrow">{t("home.location.eyebrow")}</p>
              <h2>{t("landing.locationTitle")}</h2>
              <p>{t("landing.locationText")}</p>
              <p className="landing-address">
                <MapPin size={18} /> {t("landing.office", { office: SITE.office })}
              </p>
              <CallButton className="btn btn--lg" source="landing-lokacija">
                {t("landing.schedule")}
              </CallButton>
            </div>
            <div className="map-frame">
              <iframe title={t("home.location.mapTitle")} src={mapEmbedSrc(SITE.projectMapQuery)} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </div>
        </section>
      </main>

      <Footer minimal />
      <MobileActionBar inquiryHref="#upit" />

      {inquiryUnit && (
        <Modal title={t("home.modalTitle")} onClose={() => setInquiryUnit(null)}>
          <InquiryForm
            source="landing"
            idPrefix="landing-modal"
            unit={unitSummary(inquiryUnit, projectById[inquiryUnit.projectId])}
            unitLabel={f.unitSummary(inquiryUnit, projectById[inquiryUnit.projectId])}
            submitLabel={t("landing.submit")}
            onSuccess={goToThanks}
          />
        </Modal>
      )}
      {gallery && <Lightbox images={gallery.images} title={gallery.title} onClose={() => setGallery(null)} />}
    </div>
  );
}
